import React from "react"
import { Checkbox } from "~/react/ui/checkbox"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Company } from "~/types/company"
import defaultCompanyLogoUrl from "~/images/default_company_logo.svg"
import { cn } from "~/lib/utils"
import { Tag } from "@/react/ui/tag"

// Context for sharing company data
const CompanyItemContext = React.createContext<{
  company: Company
  selected: boolean
} | null>(null)

const useCompanyItemContext = () => {
  const context = React.useContext(CompanyItemContext)
  if (!context) {
    throw new Error("CompanyItem components must be used within CompanyItemRoot")
  }
  return context
}

// Root component
interface CompanyItemRootProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect"> {
  company: Company
  selected: boolean
  onSelect?: (id: number) => void
}

const CompanyItemRoot = React.forwardRef<HTMLDivElement, CompanyItemRootProps>(
  ({ company, selected, onSelect, className, children, ...props }, ref) => {
    return (
      <CompanyItemContext.Provider value={{ company, selected }}>
        <div ref={ref} className={cn("company-item", className)} {...props}>
          {children}
        </div>
      </CompanyItemContext.Provider>
    )
  },
)
CompanyItemRoot.displayName = "CompanyItemRoot"

// Checkbox component
interface CompanyItemCheckboxProps {
  onSelect?: (id: number) => void
}

const CompanyItemCheckbox: React.FC<CompanyItemCheckboxProps> = ({ onSelect }) => {
  const { company, selected } = useCompanyItemContext()

  if (!onSelect) return null

  return (
    <div className="company-item__checkbox">
      <Checkbox checked={selected} onChange={() => onSelect(company.id)} id={`company-${company.id}`} />
    </div>
  )
}
CompanyItemCheckbox.displayName = "CompanyItemCheckbox"

// Logo component
interface CompanyItemLogoProps {
  className?: string
}

const CompanyItemLogo: React.FC<CompanyItemLogoProps> = ({ className }) => {
  const { company } = useCompanyItemContext()
  const [logoError, setLogoError] = React.useState(false)
  const logoUrl = `https://logo.clearbit.com/${company.value}`

  return (
    <img
      className={cn("company-item__logo", className)}
      src={logoError ? defaultCompanyLogoUrl : logoUrl}
      onError={() => setLogoError(true)}
      alt={company.company_name || company.value}
    />
  )
}
CompanyItemLogo.displayName = "CompanyItemLogo"

// Name component
interface CompanyItemNameProps extends React.HTMLAttributes<HTMLDivElement> {}

const CompanyItemName: React.FC<CompanyItemNameProps> = ({ className, ...props }) => {
  const { company } = useCompanyItemContext()
  return (
    <div className={cn("company-item__name", className)} {...props}>
      {company.company_name || company.value}
    </div>
  )
}
CompanyItemName.displayName = "CompanyItemName"

// Links component (website and LinkedIn)
interface CompanyItemLinksProps {
  websiteUrl?: string
  linkedinUrl?: string
}

const CompanyItemLinks: React.FC<CompanyItemLinksProps> = ({ websiteUrl, linkedinUrl }) => {
  const { company } = useCompanyItemContext()
  const website = websiteUrl || `https://${company.value}`
  const linkedin = linkedinUrl

  return (
    <div className="company-item__links">
      <a
        href={website}
        target="_blank"
        rel="noopener noreferrer"
        className="company-item__link"
        aria-label="Visit website"
      >
        <Icon icon="globe" size="sm" />
      </a>
      {linkedin && (
        <a
          href={linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="company-item__link"
          aria-label="Visit LinkedIn"
        >
          <Icon icon="linkedin" variant="brand" size="sm" />
        </a>
      )}
    </div>
  )
}
CompanyItemLinks.displayName = "CompanyItemLinks"

// Email count component
interface CompanyItemEmailCountProps {
  count?: number
}

const CompanyItemEmailCount: React.FC<CompanyItemEmailCountProps> = ({ count }) => {
  const { company } = useCompanyItemContext()
  const emailCount = count ?? company.number_of_emails

  if (!emailCount || emailCount === 0) return null

  return (
    <Tag>
      {emailCount} {emailCount === 1 ? "email address" : "email addresses"}
    </Tag>
  )
}
CompanyItemEmailCount.displayName = "CompanyItemEmailCount"

// Status indicators component
interface CompanyItemStatusProps {
  indicators?: number
}

const CompanyItemStatus: React.FC<CompanyItemStatusProps> = ({ indicators = 3 }) => {
  return (
    <div className="company-item__status">
      {Array.from({ length: indicators }).map((_, index) => (
        <span key={index} className="company-item__status-dot" />
      ))}
    </div>
  )
}
CompanyItemStatus.displayName = "CompanyItemStatus"

// Meta item component
interface CompanyItemMetaProps {
  icon: string
  children: React.ReactNode
}

const CompanyItemMeta: React.FC<CompanyItemMetaProps> = ({ icon, children }) => {
  return (
    <li className="company-item__meta">
      <Icon icon={icon} className="company-item__meta-icon" size="sm" />
      {children}
    </li>
  )
}
CompanyItemMeta.displayName = "CompanyItemMeta"

// Meta list component
interface CompanyItemMetaListProps extends React.HTMLAttributes<HTMLUListElement> {}

const CompanyItemMetaList: React.FC<CompanyItemMetaListProps> = ({ className, children, ...props }) => {
  return (
    <ul className={cn("company-item__metas", className)} {...props}>
      {children}
    </ul>
  )
}
CompanyItemMetaList.displayName = "CompanyItemMetaList"

// Header component
interface CompanyItemHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const CompanyItemHeader: React.FC<CompanyItemHeaderProps> = ({ className, children, ...props }) => {
  return (
    <div className={cn("company-item__header", className)} {...props}>
      {children}
    </div>
  )
}
CompanyItemHeader.displayName = "CompanyItemHeader"

// Content component
interface CompanyItemContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const CompanyItemContent: React.FC<CompanyItemContentProps> = ({ className, children, ...props }) => {
  return (
    <div className={cn("company-item__content", className)} {...props}>
      {children}
    </div>
  )
}
CompanyItemContent.displayName = "CompanyItemContent"

// Save button component (appears on hover)
interface CompanyItemSaveButtonProps {
  onSave?: (id: number) => void
  label?: string
}

const CompanyItemSaveButton: React.FC<CompanyItemSaveButtonProps> = ({ onSave, label = "Save" }) => {
  const { company } = useCompanyItemContext()

  if (!onSave) return null

  return (
    <div className="company-item__save">
      <Button variant="secondary" size="sm" onClick={() => onSave(company.id)} className="company-item__save-button">
        {label}
      </Button>
    </div>
  )
}
CompanyItemSaveButton.displayName = "CompanyItemSaveButton"

// Primary info section component
interface CompanyItemInfoProps extends React.HTMLAttributes<HTMLDivElement> {}

const CompanyItemInfo: React.FC<CompanyItemInfoProps> = ({ className, children, ...props }) => {
  return (
    <div className={cn("company-item__info", className)} {...props}>
      {children}
    </div>
  )
}
CompanyItemInfo.displayName = "CompanyItemInfo"

// Default composed component for backward compatibility
interface CompanyItemProps {
  company: Company
  selected: boolean
  onSelect?: (id: number) => void
  onSave?: (id: number) => void
  showLinks?: boolean
  showStatus?: boolean
  showEmailCount?: boolean
  linkedinUrl?: string
}

export const CompanyItem: React.FC<CompanyItemProps> = ({
  company,
  selected,
  onSelect,
  onSave,
  showLinks = true,
  showStatus = true,
  showEmailCount = true,
  linkedinUrl,
}) => {
  return (
    <CompanyItemRoot company={company} selected={selected} onSelect={onSelect}>
      <CompanyItemCheckbox onSelect={onSelect} />
      <CompanyItemContent>
        <CompanyItemHeader>
          <CompanyItemLogo />
          <CompanyItemInfo>
            <CompanyItemName />
            {showLinks && <CompanyItemLinks linkedinUrl={linkedinUrl} />}
          </CompanyItemInfo>
          <div className="company-item__header-right">
            {showEmailCount && <CompanyItemEmailCount />}
            {showStatus && <CompanyItemStatus />}
          </div>
          <CompanyItemSaveButton onSave={onSave} />
        </CompanyItemHeader>
        {(company.size || company.category || company.address) && (
          <CompanyItemMetaList>
            {company.size && <CompanyItemMeta icon="users">{company.size}</CompanyItemMeta>}
            {company.category && <CompanyItemMeta icon="buildings">{company.category}</CompanyItemMeta>}
            {company.address && <CompanyItemMeta icon="map-location-dot">{company.address}</CompanyItemMeta>}
          </CompanyItemMetaList>
        )}
      </CompanyItemContent>
    </CompanyItemRoot>
  )
}

// Export all components for composition
export {
  CompanyItemRoot,
  CompanyItemCheckbox,
  CompanyItemLogo,
  CompanyItemHeader,
  CompanyItemInfo,
  CompanyItemName,
  CompanyItemLinks,
  CompanyItemEmailCount,
  CompanyItemStatus,
  CompanyItemMeta,
  CompanyItemMetaList,
  CompanyItemContent,
  CompanyItemSaveButton,
}
